import * as yup from 'yup'
import {
  ISelectInput,
  PreviewOptions,
  IEditionMetadataInput,
  IPackInput,
  FormObjectType,
} from '../../types/pack'
import { getPreviewImages } from '../../services/pack'
import { VALIDATION_PATTERNS } from '../../config'

const embedDeclarationBaseSchema = yup
  .string()
  .trim()
  .max(1000, 'Declaration is too long, maximun number of characters is 1000')

export const packValidationSchema = yup.object({
  title: yup
    .string()
    .trim()
    .max(246, 'The text is too long, maximum number of characters is 246.')
    .required('Title is required'),
  description: yup
    .string()
    .trim()
    .max(255, 'The text is too long, maximum number of characters is 255.')
    .required('Caption is required'),
  byline: yup
    .string()
    .trim()
    .max(255, 'The text is too long, maximum number of characters is 255.')
    .required('Byline is required'),
  language: yup.string().trim().required('Language is required'),
  slugline: yup
    .string()
    .trim()
    .matches(
      VALIDATION_PATTERNS.VALID_SLUG_CHARACTERS,
      'Slug contains invalid characters'
    )
    .matches(VALIDATION_PATTERNS.SLUG, 'Invalid slug format')
    .max(64, 'The text is too long, maximum number of characters is 64.')
    .required('Slug is required'),
  location: yup.object().nullable().required('Location is required'),
  topicCodes: yup.array().min(1, 'Topic is required'),
  contacts: yup
    .array()
    .max(10, 'The number of editors should not be more than 10'),
  editions: yup.array().of(
    yup.object().shape({
      addToPackSlug: yup.boolean(),
      embed: yup.object().shape({
        declaration: yup
          .string()
          .when('dependencies', (dependencies) =>
            dependencies?.length > 0
              ? embedDeclarationBaseSchema.required('Declaration is required')
              : embedDeclarationBaseSchema
          ),
        dependencies: yup
          .string()
          .max(
            1000,
            'The text is too long, maximun number of characters is 1000'
          ),
      }),
    })
  ),
})

export const validateEditionSlug = (isWild = false, packSlug = '') => (
  value: string
): string | undefined => {
  const packSlugLength = packSlug ? packSlug.length : 0
  if (isWild && value.length && value.length + packSlugLength > 64) {
    return 'The combination of the pack and file slugline is too long, maximum number of characters is 64.'
  }
  if (!isWild && value.length && value.length > 64) {
    return 'Slug is too long, maximum number of characters is 64.'
  }
  if (!value && !isWild) {
    return 'Slug is required'
  } else if (!VALIDATION_PATTERNS.VALID_SLUG_CHARACTERS.test(value)) {
    return 'Slug contains invalid characters'
  } else if (!VALIDATION_PATTERNS.SLUG.test(value) && !isWild) {
    return 'Invalid slug format'
  } else if (!VALIDATION_PATTERNS.SLUG_WILD.test(value) && isWild) {
    return 'Invalid wild slug format'
  }
}

export const validateEditionCaption = (
  isLanguageInherit = true,
  packCaption = ''
) => (value: string): string | undefined => {
  const packCaptionLength = packCaption ? packCaption.length : 0
  if (
    isLanguageInherit &&
    value.length &&
    value.length + packCaptionLength > 255
  ) {
    return 'The combination of the pack and file caption is too long, maximum number of characters is 255.'
  }
  if (!isLanguageInherit && value.length && value.length > 255) {
    return 'The text is too long, maximum number of characters is 255.'
  }
  if (!isLanguageInherit && !value.trim()) {
    return 'File caption is required'
  }
}

export const validateEditionTitle = (
  isLanguageInherit = true,
  packTitle = ''
) => (value: string): string | undefined => {
  const packTitleLength = packTitle ? packTitle.length : 0
  if (!isLanguageInherit && !value.trim()) {
    return 'File title is required'
  }
  if (
    isLanguageInherit &&
    value.length &&
    value.length + packTitleLength > 246
  ) {
    return 'The combination of the pack and file title is too long, maximum number of characters is 246.'
  }
  if (!isLanguageInherit && value.length && value.length > 246) {
    return 'The text is too long, maximum number of characters is 246.'
  }
}

export const validateTags = (tags: ISelectInput[]): string | undefined => {
  if (tags.some((tag) => tag.value.length > 50)) {
    return 'The tag is too long, maximum number of characters is 50.'
  }
}

export const validateLocation = async (value: FormObjectType) => {
  const locationSchema = yup.object({
    rcsId: yup.string().trim().max(255).required(),
    n2000: yup.array().of(yup.string().trim().max(255)).min(1).nullable(),
    shortName: yup.string().trim().max(255).required(),
    additionalCodes: yup.array().of(yup.string().trim().max(255)).nullable(),
    parentRcsId: yup.array().of(yup.string().trim().max(255)).nullable(),
    facetCode: yup.array().of(yup.string().trim().max(255)).required(),
    conceptType: yup.number().integer().required(),
  })
  try {
    await locationSchema.validate(value)
  } catch (err) {
    return 'Location is incomplete'
  }
}

export const packPreviewValidate = (pack: IPackInput) => (
  value: PreviewOptions
): string | undefined => {
  const validCases = [
    value === PreviewOptions.useGraphicPreview &&
      (getPreviewImages(pack).previewURI ||
        pack.editions.length === 0 ||
        pack.editions.some(
          (edition) =>
            edition.useGraphicPreview === PreviewOptions.useUploadedPreview &&
            !!getPreviewImages(edition).newTmpPreview
        )),
    value === PreviewOptions.useUploadedPreview &&
      getPreviewImages(pack).newTmpPreview,
  ]
  if (!validCases.some(Boolean)) {
    return 'Please upload a preview to save and publish the pack'
  }
}

export const editionPreviewValidate = (edition: IEditionMetadataInput) => (
  value: PreviewOptions
): string | undefined => {
  const validCases = [
    value === PreviewOptions.useGraphicPreview,
    value === PreviewOptions.useFilePreview &&
      getPreviewImages(edition).previewURI,
    value === PreviewOptions.useUploadedPreview &&
      getPreviewImages(edition).newTmpPreview,
  ]
  if (!validCases.some(Boolean)) {
    return 'Please upload a preview to save and publish the edition'
  }
}
